// SPDX-License-Identifier: AGPL-3.0-or-later

import type {GatewayHandlerContext} from '@app/features/gateway/events/EventRouter';
import type {VoiceState} from '@app/features/gateway/types/GatewayVoiceTypes';
import GuildMembers from '@app/features/member/state/GuildMembers';
import {SoundType} from '@app/features/notification/utils/SoundUtils';
import * as SoundCommands from '@app/features/ui/commands/SoundCommands';
import Users from '@app/features/user/state/Users';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import VoiceConnectionManager from '@app/features/voice/engine/VoiceConnectionManager';
import VoiceStateManager from '@app/features/voice/engine/VoiceStateManager';
import type {GuildMemberData} from '@fluxer/schema/src/domains/guild/GuildMemberSchemas';

interface VoiceStateUpdatePayload {
	user_id: string;
	channel_id?: string | null;
	guild_id?: string | null;
	session_id?: string;
	connection_id?: string;
	self_mute?: boolean;
	self_deaf?: boolean;
	self_video?: boolean;
	self_stream?: boolean;
	mute?: boolean;
	deaf?: boolean;
	suppress?: boolean;
}

function shouldPlayJoinChime(data: VoiceStateUpdatePayload): boolean {
	if (!VoiceConnectionManager.connected) return false;
	if (!data.channel_id) return false;
	if (data.channel_id !== VoiceConnectionManager.channelId) return false;
	const currentUser = Users.getCurrentUser();
	if (currentUser && data.user_id === currentUser.id) return false;
	const previousState = data.connection_id ? VoiceStateManager.getVoiceStateByConnectionId(data.connection_id) : null;
	if (previousState?.channel_id === data.channel_id) return false;
	return true;
}

export function handleVoiceStateUpdate(data: VoiceStateUpdatePayload, _context: GatewayHandlerContext): void {
	const guildId = data.guild_id ?? null;
	const voiceState = data as VoiceState;
	if (guildId && voiceState.member) {
		GuildMembers.hydrateIfMissing(guildId, voiceState.member as GuildMemberData);
	}
	const playChime = shouldPlayJoinChime(data);
	MediaEngine.handleGatewayVoiceStateUpdate(guildId, voiceState);
	if (playChime) {
		SoundCommands.playSound(SoundType.UserJoin);
	}
}
